import { Fragment, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  computeGuestStats,
  giftStats,
  groupByHousehold,
  guestDisplayName,
  isSolo,
  owesThankYou,
  AGE_BANDS,
  GUEST_LIKELIHOODS,
  RSVP_STATUSES,
  type Guest,
  type GuestLikelihood,
  type HouseholdGroup,
  type RsvpStatus,
  type Wedding,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Menu } from '@/components/ui/Menu';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pill, rsvpTone } from '@/components/ui/Pill';
import { Metric, MetricFootnote } from '@/components/ui/Stat';
import { FilterPill } from '@/components/ui/FilterPill';
import { TextInput } from '@/components/ui/Field';
import { ApiRequestError } from '@/lib/api';
import { GuestBreakdown } from './GuestBreakdown';
import { GuestDrawer } from './GuestDrawer';
import { HouseholdDrawer } from './HouseholdDrawer';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ClearGuestsDialog } from './ClearGuestsDialog';
import { HouseholdName } from './HouseholdName';
import { ImportReview } from './ImportReview';
import { stripVcard } from './vcardStrip';
import { prepareImage } from '@/lib/image';
import { useRsvpLink } from '@/features/site/queries';
import {
  downloadGuestCsv,
  downloadGuestTemplate,
  useAiUsage,
  useDeleteHousehold,
  useExtractFromPhoto,
  useGuests,
  useHouseholds,
  useParseImport,
  useSetHouseholdRsvp,
  useUpdateGuest,
  type ImportProposal,
  type ImportReport,
} from './queries';

/** Matches a guest on name, contact or any tag. */
function matches(g: Guest, q: string): boolean {
  const hay = [g.firstName, g.lastName, g.email, g.phone, ...(g.tags ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

/** Searching "Rivera" should show the whole family, not half of it. */
function groupMatches(group: HouseholdGroup, q: string): boolean {
  if (!q) return true;
  if (group.household.label.toLowerCase().includes(q)) return true;
  return group.members.some((m) => matches(m, q));
}

/**
 * What a shut household row has to say for itself.
 *
 * Collapsing is only a gain if the closed row still answers the question. One
 * status for everyone is a pill; anything else is the count that matters, which
 * is how many said yes.
 */
function rsvpSummary(members: { rsvp: RsvpStatus }[]): {
  mixed: boolean;
  only: RsvpStatus;
  attending: number;
} {
  const first = members[0]?.rsvp ?? 'pending';
  return {
    mixed: members.some((m) => m.rsvp !== first),
    only: first,
    attending: members.filter((m) => m.rsvp === 'attending').length,
  };
}

/**
 * The household's personal RSVP link, minted the first time it is asked for.
 *
 * Copy rather than show: the token is the whole of the authentication, and a
 * URL printed on screen is a URL that ends up in a screenshot. Pressing it again
 * copies the same link; "Make a new one" mints a fresh token and the old link
 * stops working, which is the only revocation there is once a link has been
 * forwarded to the wrong person.
 */
function RsvpLinkButton({
  wedId,
  householdId,
  existing,
}: {
  wedId: string;
  householdId: string;
  existing?: string;
}) {
  const { t } = useTranslation();
  const mint = useRsvpLink(wedId);
  const [copied, setCopied] = useState(false);

  const copy = async (token: string) => {
    const url = `${window.location.origin}/r/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in some browsers and in any insecure
      // context. Showing the URL is worse than nothing happening, so prompt()
      // lets them copy it by hand rather than losing the link entirely.
      window.prompt(t('site.copyLink'), url);
    }
  };

  return (
    <button
      type="button"
      onClick={() => {
        if (existing) void copy(existing);
        else mint.mutate({ householdId }, { onSuccess: (res) => void copy(res.token) });
      }}
      className="min-h-tap text-caption text-brass-text underline underline-offset-4"
    >
      {copied ? t('site.linkCopied') : t('site.copyLink')}
    </button>
  );
}

/**
 * The couple's guess at whether somebody will come, as opposed to what that
 * person has replied.
 *
 * Shown on every row including the ones who have answered, because a guess made
 * before a reply is still worth seeing afterwards — and hiding it on answered
 * rows would make the column flicker in and out as RSVPs arrive.
 */
function LikelihoodSelect({
  guest,
  onChange,
}: {
  guest: Guest;
  onChange: (guest: Guest, value: GuestLikelihood) => void;
}) {
  const { t } = useTranslation();
  const value = guest.likelihood ?? 'unsure';
  return (
    <label>
      <span className="sr-only">{t('guests.likelihood')}</span>
      <select
        value={value}
        onChange={(e) => onChange(guest, e.target.value as GuestLikelihood)}
        className={`min-h-tap rounded border border-line bg-paper px-2 text-caption ${
          value === 'unlikely' ? 'text-leaf-soft' : ''
        }`}
      >
        {GUEST_LIKELIHOODS.map((option) => (
          <option key={option} value={option}>
            {t(`guests.likelihoodValue.${option}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function GuestsTab() {
  const { t, i18n } = useTranslation();
  const { wedding, canWrite } = useOutletContext<{ wedding: Wedding; canWrite: boolean }>();
  const wedId = wedding.wedId;

  const guests = useGuests(wedId);
  const households = useHouseholds(wedId);
  const update = useUpdateGuest(wedId);
  const setRsvpForAll = useSetHouseholdRsvp(wedId);
  const removeHousehold = useDeleteHousehold(wedId);
  const parseImport = useParseImport(wedId);
  const extractPhoto = useExtractFromPhoto(wedId);
  const csvInput = useRef<HTMLInputElement>(null);
  const vcfInput = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [rsvpFilter, setRsvpFilter] = useState<RsvpStatus | 'all'>('all');
  /**
   * Households start closed, so the list reads as one line per invitation
   * rather than one per person. Only the opened ones are held here.
   */
  const [openHouseholds, setOpenHouseholds] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Guest | undefined>();
  const [addingGuest, setAddingGuest] = useState(false);
  const [addingHousehold, setAddingHousehold] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [pendingHousehold, setPendingHousehold] = useState<{
    householdId: string;
    label: string;
    count: number;
  }>();
  const [report, setReport] = useState<ImportReport>();
  const [proposal, setProposal] = useState<ImportProposal>();
  const [importing, setImporting] = useState<string>();
  const [importError, setImportError] = useState<string>();
  // Read on the guest list rather than after a failed import: the point of
  // showing the allowance is to know before photographing, not after.
  const aiUsage = useAiUsage(wedId, true);

  // Shown on the menu item rather than as a line on the page: the allowance only
  // matters at the moment you are choosing to spend it.
  const photoHint =
    aiUsage.data && aiUsage.data.budget !== null
      ? t('import.budgetLeft', {
          photos: Math.floor((aiUsage.data.remaining ?? 0) / aiUsage.data.estimatePerPhoto),
        })
      : t('import.fromPhotoHint');

  const all = guests.data ?? [];
  const householdList = households.data ?? [];
  const gifts = giftStats(householdList, new Date().toISOString().slice(0, 10));

  // One pure function, shared with the API, so the numbers cannot drift apart.
  const stats = useMemo(() => computeGuestStats(all), [all]);

  const toggle = (householdId: string) =>
    setOpenHouseholds((prev) => {
      const next = new Set(prev);
      if (next.has(householdId)) next.delete(householdId);
      else next.add(householdId);
      return next;
    });

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groupByHousehold(all, householdList)
      .filter((g) => groupMatches(g, q))
      .map((g) => ({
        ...g,
        members: g.members.filter((m) => rsvpFilter === 'all' || m.rsvp === rsvpFilter),
      }))
      .filter((g) => g.members.length > 0);
  }, [all, householdList, query, rsvpFilter]);

  /** Inline RSVP: the edit this tab exists for, so it must not cost a drawer. */
  async function setRsvp(g: Guest, rsvp: RsvpStatus) {
    await update.mutateAsync({ guestId: g.guestId, input: { rsvp, updatedAt: g.updatedAt } });
  }

  /** The couple's own guess, edited the same way and just as cheaply. */
  async function setLikelihood(g: Guest, likelihood: GuestLikelihood) {
    await update.mutateAsync({
      guestId: g.guestId,
      input: { likelihood, updatedAt: g.updatedAt },
    });
  }

  /** A spreadsheet or a contacts export: parsed on the server, reviewed before saving. */
  async function onTextFile(file: File, format: 'csv' | 'vcard') {
    setReport(undefined);
    setImportError(undefined);
    setImporting(format === 'vcard' ? t('import.readingContacts') : t('import.readingFile'));
    try {
      const raw = await file.text();
      // Addresses and birthdays are dropped here, on the device, so they are
      // never uploaded at all.
      const text = format === 'vcard' ? stripVcard(raw) : raw;
      setProposal(await parseImport.mutateAsync({ format, text }));
    } catch {
      setImportError(t('import.couldNotRead'));
    } finally {
      setImporting(undefined);
      if (csvInput.current) csvInput.current.value = '';
      if (vcfInput.current) vcfInput.current.value = '';
    }
  }

  /** One photo per request, so each stays inside the model call's time budget. */
  async function onPhotos(files: File[]) {
    setReport(undefined);
    setImportError(undefined);
    const merged: ImportProposal['households'] = [];
    const errors: ImportProposal['errors'] = [];

    for (const [i, file] of files.entries()) {
      setImporting(t('import.readingPhoto', { current: i + 1, total: files.length }));
      try {
        const image = await prepareImage(file);
        const result = await extractPhoto.mutateAsync({ image });
        merged.push(...result.households);
        errors.push(...result.errors);
      } catch (err) {
        const code = err instanceof ApiRequestError ? err.code : undefined;
        // Out of allowance, or the model is unreachable: every remaining photo
        // would fail the same way, so stop and say what the server said rather
        // than blaming each picture in turn.
        if (code === 'ai_quota_exceeded' || code === 'ai_unavailable') {
          setImportError(err instanceof Error ? err.message : t('import.outOfBudget'));
          break;
        }
        errors.push({
          line: 0,
          message:
            err instanceof ApiRequestError && err.status === 504
              ? t('import.photoTooSlow')
              : t('import.photoFailed', { n: i + 1 }),
        });
      }
    }

    setImporting(undefined);
    if (photoInput.current) photoInput.current.value = '';
    if (merged.length > 0 || errors.length > 0) {
      setProposal({
        households: merged,
        errors,
        ignoredColumns: [],
        people: merged.reduce((n, h) => n + h.members.length, 0),
        source: 'photo',
        defaultSelection: 'all',
      });
    }
  }

  const forceOpen = query.trim() !== '' || rsvpFilter !== 'all';
  // Solo guests have nothing to open, so they are not counted by the all-toggle.
  const expandable = groups.filter((g) => !isSolo(g));
  const allOpen =
    expandable.length > 0 && expandable.every((g) => openHouseholds.has(g.household.householdId));

  /*
   * Everyone in the list, not everyone who has replied.
   *
   * This read `attendingByAgeBand`, so a list where nobody has answered yet
   * said "0 children" beside seven of them. The two numbers it sits with —
   * total people and households — are counts of the whole list, and a line that
   * changes population halfway along cannot be read correctly by anybody.
   *
   * `attendingByAgeBand` is the right number for catering, which is a question
   * worth answering once RSVPs exist and somewhere other than here.
   */
  const kidsSub = AGE_BANDS.filter((a) => a !== 'adult')
    .map((a) => `${stats.byAgeBand[a] ?? 0} ${t(`guests.ageBandPlural.${a}`)}`)
    .join(' · ');

  const hostName = (g: Guest): string | undefined => {
    if (!g.placeholderFor) return undefined;
    const host = all.find((x) => x.guestId === g.placeholderFor);
    return host ? host.firstName : undefined;
  };

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">{t('nav.guests')}</p>
          <h1 className="font-display text-display">{t('guests.heading')}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Export stays for everyone — reading the list is what a read-only
              member is here for. Everything that writes is hidden rather than
              disabled: a greyed row of buttons is an app telling you about
              permissions you did not ask about. */}
          {canWrite && (
            <>
              {/* One trigger, sectioned — six equal buttons said nothing about which
              one you wanted, and three of them were only ever used once. */}
              <Menu
                trigger={t('guests.addMenu')}
                sections={[
                  {
                    label: t('guests.addByHand'),
                    items: [
                      {
                        label: t('guests.householdAdd'),
                        hint: t('guests.householdAddHint'),
                        onSelect: () => setAddingHousehold(true),
                      },
                      {
                        label: t('guests.add'),
                        hint: t('guests.addHint'),
                        onSelect: () => setAddingGuest(true),
                      },
                    ],
                  },
                  {
                    label: t('import.section'),
                    items: [
                      {
                        // Before "choose a file", because the order is: get the
                        // file, fill it, bring it back. Offering the import first
                        // and the template underneath has people guessing at
                        // columns and then finding out there was a template.
                        label: t('import.template'),
                        hint: t('import.templateHint'),
                        onSelect: () => downloadGuestTemplate(i18n.language),
                      },
                      {
                        label: t('import.fromFile'),
                        hint: t('import.fromFileHint'),
                        onSelect: () => csvInput.current?.click(),
                      },
                      {
                        label: t('import.fromContacts'),
                        hint: t('import.fromContactsHint'),
                        onSelect: () => vcfInput.current?.click(),
                      },
                      {
                        label: t('import.fromPhoto'),
                        hint: photoHint,
                        onSelect: () => photoInput.current?.click(),
                      },
                    ],
                  },
                ]}
              />
              <Button variant="ghost" onClick={() => void downloadGuestCsv(wedId)}>
                {t('guests.export')}
              </Button>
              {/* Next to Export on purpose: the only way to undo this is to have
              downloaded the list first, and that button is right there. */}
              {all.length > 0 && (
                <Button variant="ghost" onClick={() => setClearing(true)}>
                  {t('guests.clearAction')}
                </Button>
              )}
            </>
          )}
          {/* Separate inputs, so the file picker filters to what each one means. */}
          <input
            ref={csvInput}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onTextFile(f, 'csv');
            }}
          />
          <input
            ref={vcfInput}
            type="file"
            accept=".vcf,text/vcard,text/x-vcard"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onTextFile(f, 'vcard');
            }}
          />
          <input
            ref={photoInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              if (files.length > 0) void onPhotos(files);
            }}
          />
        </div>
      </div>

      {/* Two numbers lead, the rest support. Seven panels of equal weight
          answered nothing first. */}
      <div className="panel flex flex-col gap-5 p-5">
        <div className="flex flex-wrap gap-6">
          <Metric
            label={t('guests.confirmed')}
            value={stats.confirmedHeadcount}
            sub={t('guests.confirmedSub')}
          />
          <Metric
            label={t('guests.anticipated')}
            value={stats.anticipatedHeadcount}
            sub={t('guests.anticipatedSub')}
          />
          {/* Only once somebody has been judged. Before that it is the
              anticipated number again, and a third panel of the same figure
              teaches people to stop reading the panels. */}
          {stats.expectedHeadcount !== stats.anticipatedHeadcount && (
            <Metric
              label={t('guests.expected')}
              value={stats.expectedHeadcount}
              sub={t('guests.expectedSub')}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <MetricFootnote
            items={[
              t('guests.peopleCount', { count: stats.total }),
              t('guests.householdCount', { count: stats.households }),
              kidsSub || undefined,
            ]}
          />
          {stats.total > 0 && <GuestBreakdown stats={stats} />}
          {/* Only once a gift has arrived. Before the wedding this line is
              noise on every screen, and after it is the only thing anybody
              opens this tab for. */}
          {gifts.received > 0 && (
            <MetricFootnote
              items={[
                t('gifts.received', { count: gifts.received }),
                gifts.owed > 0 && t('gifts.pendingCount', { count: gifts.owed }),
                gifts.owed > 0 &&
                  gifts.longestWait > 0 &&
                  t('gifts.longestWait', { days: gifts.longestWait }),
                gifts.overdue > 0 && t('gifts.overdue', { count: gifts.overdue }),
              ]}
            />
          )}
        </div>
      </div>

      {importing && (
        <p className="text-body text-leaf-soft" role="status">
          {importing}
        </p>
      )}

      {importError && (
        <p className="text-body text-status-declinedFg" role="alert">
          {importError}
        </p>
      )}

      {report && (
        <div className="panel flex flex-col gap-2 p-4" role="status">
          <p className="text-body">
            {t('guests.imported', { count: report.created })}{' '}
            {t('guests.importedHouseholds', { count: report.households })}
          </p>
          {report.ignoredColumns.length > 0 && (
            <p className="text-caption text-leaf-soft">
              {t('guests.ignoredColumns')}: {report.ignoredColumns.join(', ')}
            </p>
          )}
          {report.errors.length > 0 && (
            <details>
              <summary className="cursor-pointer text-caption text-status-declinedFg">
                {t('guests.rowsSkipped', { count: report.errors.length })}
              </summary>
              <ul className="mt-2 flex flex-col gap-1">
                {report.errors.slice(0, 50).map((e) => (
                  <li key={`${e.line}-${e.message}`} className="text-caption text-leaf-soft">
                    {t('guests.line')} {e.line}: {e.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <label className="flex-1">
          <span className="sr-only">{t('guests.search')}</span>
          <TextInput
            type="search"
            placeholder={t('guests.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <FilterPill
            label={t('guests.all')}
            count={stats.total}
            selected={rsvpFilter === 'all'}
            onSelect={() => setRsvpFilter('all')}
          />
          {RSVP_STATUSES.map((s) => (
            <FilterPill
              key={s}
              label={t(`guests.rsvpValue.${s}`)}
              count={stats.byRsvp[s] ?? 0}
              selected={rsvpFilter === s}
              onSelect={() => setRsvpFilter(s)}
            />
          ))}
        </div>
      </div>

      {/* Hidden while a search or filter is forcing everything open, because the
          control would not do anything. */}
      {!forceOpen && expandable.length > 0 && (
        <div className="-mb-2 flex justify-end">
          <button
            type="button"
            onClick={() =>
              setOpenHouseholds(
                allOpen ? new Set() : new Set(expandable.map((g) => g.household.householdId)),
              )
            }
            className="min-h-tap text-caption text-leaf-soft underline underline-offset-4 hover:text-leaf"
          >
            {allOpen ? t('guests.collapseAll') : t('guests.expandAll')}
          </button>
        </div>
      )}

      {(guests.isLoading || households.isLoading) && (
        <p className="text-body text-leaf-soft">{t('common.loading')}</p>
      )}
      {guests.isError && <p className="text-body text-leaf-soft">{t('common.somethingWrong')}</p>}

      {guests.data && groups.length === 0 && (
        <EmptyState
          title={stats.total === 0 ? t('empty.guestsTitle') : t('guests.noMatchTitle')}
          body={stats.total === 0 ? t('empty.guestsBody') : t('guests.noMatchBody')}
          action={
            stats.total === 0 && canWrite ? (
              <div className="flex gap-2">
                <Button onClick={() => setAddingHousehold(true)}>{t('guests.householdAdd')}</Button>
                <Button variant="secondary" onClick={() => setAddingGuest(true)}>
                  {t('guests.add')}
                </Button>
              </div>
            ) : undefined
          }
        />
      )}

      {groups.length > 0 && (
        <div className="panel overflow-x-auto">
          {/*
            Six columns do not fit on a phone, and a table that forces the page
            to pan sideways reads as broken rather than as wide. So age, contact
            and tags leave the grid below `md` and reappear stacked under the
            name — the same cells, moved, rather than a second markup tree that
            would drift out of step with this one.
          */}
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-3 py-3 text-caption font-medium sm:px-4">{t('guests.name')}</th>
                <th className="px-3 py-3 text-caption font-medium sm:px-4">{t('guests.rsvp')}</th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('guests.likelihood')}
                </th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('guests.ageBand')}
                </th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('guests.contact')}
                </th>
                <th className="hidden px-4 py-3 text-caption font-medium md:table-cell">
                  {t('guests.tags')}
                </th>
                <th className="px-3 py-3 sm:px-4" />
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => {
                const hh = group.household;
                // A lone guest needs no group chrome — that is what keeps a
                // household of one invisible.
                const solo = isSolo(group);
                // A search or a filter has already decided these rows are the
                // interesting ones. Leaving them shut would hide the very match
                // that put them on screen.
                const open = solo || forceOpen || openHouseholds.has(hh.householdId);
                const summary = rsvpSummary(group.members);
                return (
                  <Fragment key={hh.householdId}>
                    {!solo && (
                      <tr className="border-b border-line bg-cream/40">
                        <td className="px-3 py-2 sm:px-4" colSpan={4}>
                          <span className="flex min-w-0 flex-wrap items-center gap-2">
                            <HouseholdName
                              wedId={wedId}
                              household={hh}
                              count={group.members.length}
                              open={open}
                              onToggle={() => toggle(hh.householdId)}
                            />
                            {/* Closed, the names are the only thing that makes a
                                household reviewable without opening it. */}
                            {!open && (
                              <span className="min-w-0 flex-1 truncate text-caption text-leaf-soft">
                                {group.members
                                  .map((m) => guestDisplayName(m, hostName(m)))
                                  .join(', ')}
                              </span>
                            )}
                            {'address' in hh && hh.address?.city && (
                              <span className="text-caption text-leaf-soft">{hh.address.city}</span>
                            )}
                            {'invitationSentAt' in hh && hh.invitationSentAt && (
                              <Pill tone="booked">{t('guests.invited')}</Pill>
                            )}
                            {/* Only while something is owed. "Thanked" and
                                "nothing yet" are the two states you never need
                                to be told about from across the list. */}
                            {!('synthetic' in hh) && owesThankYou(hh) && (
                              <Pill tone="pending">{t('gifts.status.received')}</Pill>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right sm:px-4" colSpan={2}>
                          <span className="flex flex-wrap items-center justify-end gap-2">
                            {/* The RSVP mix, so a closed row still answers the
                                question the page is for. */}
                            {!open &&
                              (summary.mixed ? (
                                <span className="tabular whitespace-nowrap text-caption text-leaf-soft">
                                  {t('guests.rsvpMix', {
                                    count: summary.attending,
                                    total: group.members.length,
                                  })}
                                </span>
                              ) : (
                                <Pill tone={rsvpTone(summary.only)}>
                                  {t(`guests.rsvpValue.${summary.only}`)}
                                </Pill>
                              ))}
                            <label>
                              <span className="sr-only">{t('guests.setEveryone')}</span>
                              <select
                                value=""
                                onChange={(e) => {
                                  if (!e.target.value) return;
                                  void setRsvpForAll.mutateAsync({
                                    householdId: hh.householdId,
                                    input: {
                                      rsvp: e.target.value as RsvpStatus,
                                      includePlaceholders: false,
                                    },
                                  });
                                }}
                                className="min-h-tap rounded border border-line bg-paper px-2 text-caption"
                              >
                                <option value="">{t('guests.setEveryone')}</option>
                                {RSVP_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {t(`guests.rsvpValue.${s}`)}
                                  </option>
                                ))}
                              </select>
                            </label>
                            {/* Destructive, so it only appears once you have
                                opened the household and seen who is in it. */}
                            {open && (
                              <RsvpLinkButton
                                wedId={wedId}
                                householdId={hh.householdId}
                                {...('rsvpToken' in hh && hh.rsvpToken
                                  ? { existing: hh.rsvpToken }
                                  : {})}
                              />
                            )}
                            {open && (
                              <button
                                type="button"
                                onClick={() =>
                                  setPendingHousehold({
                                    householdId: hh.householdId,
                                    label: hh.label,
                                    count: group.members.length,
                                  })
                                }
                                className="min-h-tap text-caption text-status-declinedFg underline underline-offset-4"
                              >
                                {t('guests.removeHousehold')}
                              </button>
                            )}
                          </span>
                        </td>
                      </tr>
                    )}

                    {open &&
                      group.members.map((g) => (
                        <tr key={g.guestId} className="border-b border-line last:border-0">
                          <td className={`px-3 py-3 sm:px-4 ${solo ? '' : 'pl-5 sm:pl-8'}`}>
                            {g.isPlaceholder ? (
                              <span className="text-leaf-soft italic">
                                {guestDisplayName(g, hostName(g))}
                              </span>
                            ) : (
                              <span>
                                {g.firstName} {g.lastName}
                              </span>
                            )}
                            {/* The three columns that left the grid below `md`.
                                `overflow-wrap:anywhere` because an email
                                address is one long unbreakable token, and on a
                                phone it was that token, not the layout, deciding
                                how wide the name column had to be. `break-words`
                                is not enough: it wraps the token but leaves the
                                element's min-content width at the token's full
                                length, which is what a table sizes itself from. */}
                            <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-leaf-soft [overflow-wrap:anywhere] md:hidden">
                              <span>{t(`guests.ageBandValue.${g.ageBand ?? 'adult'}`)}</span>
                              <span className="md:hidden">
                                <LikelihoodSelect guest={g} onChange={setLikelihood} />
                              </span>
                              {(g.email || g.phone) && <span>{g.email || g.phone}</span>}
                              {(g.tags ?? []).map((tag) => (
                                <Pill key={tag}>{tag}</Pill>
                              ))}
                            </span>
                          </td>
                          <td className="px-3 py-3 sm:px-4">
                            <label>
                              <span className="sr-only">{t('guests.rsvp')}</span>
                              <select
                                value={g.rsvp}
                                onChange={(e) => void setRsvp(g, e.target.value as RsvpStatus)}
                                className="min-h-tap rounded border border-line bg-paper px-2 text-caption"
                              >
                                {RSVP_STATUSES.map((s) => (
                                  <option key={s} value={s}>
                                    {t(`guests.rsvpValue.${s}`)}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </td>
                          <td className="hidden px-4 py-3 md:table-cell">
                            <LikelihoodSelect guest={g} onChange={setLikelihood} />
                          </td>
                          <td className="hidden px-4 py-3 text-caption text-leaf-soft md:table-cell">
                            {t(`guests.ageBandValue.${g.ageBand ?? 'adult'}`)}
                          </td>
                          <td className="hidden px-4 py-3 text-caption text-leaf-soft md:table-cell">
                            {g.email || g.phone || '—'}
                          </td>
                          <td className="hidden px-4 py-3 md:table-cell">
                            <span className="flex flex-wrap gap-1">
                              {(g.tags ?? []).map((tag) => (
                                <Pill key={tag}>{tag}</Pill>
                              ))}
                              {g.rsvp === 'attending' && (
                                <Pill tone={rsvpTone('attending')}>✓</Pill>
                              )}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right sm:px-4">
                            <button
                              type="button"
                              onClick={() => setEditing(g)}
                              className="min-h-tap text-caption text-brass-text underline underline-offset-4"
                            >
                              {g.isPlaceholder ? t('guests.nameThem') : t('guests.edit')}
                            </button>
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {proposal && (
        <ImportReview
          wedId={wedId}
          proposal={proposal}
          open
          onClose={() => setProposal(undefined)}
          onDone={setReport}
        />
      )}

      {addingHousehold && (
        <HouseholdDrawer wedId={wedId} open onClose={() => setAddingHousehold(false)} />
      )}
      {clearing && (
        <ClearGuestsDialog
          wedId={wedId}
          weddingTitle={wedding.title}
          guestCount={all.length}
          open
          onClose={() => setClearing(false)}
        />
      )}
      {addingGuest && (
        <GuestDrawer
          canWrite={canWrite}
          wedId={wedId}
          households={householdList}
          open
          onClose={() => setAddingGuest(false)}
        />
      )}
      {editing && (
        <GuestDrawer
          canWrite={canWrite}
          key={editing.guestId + editing.updatedAt}
          wedId={wedId}
          guest={editing}
          households={householdList}
          open
          onClose={() => setEditing(undefined)}
        />
      )}

      <ConfirmDialog
        open={pendingHousehold !== undefined}
        title={t('guests.removeHouseholdTitle', { label: pendingHousehold?.label ?? '' })}
        body={
          <>
            <p>{t('guests.removeHouseholdAlso', { count: pendingHousehold?.count ?? 0 })}</p>
            <p>{t('common.cannotBeUndone')}</p>
          </>
        }
        confirmLabel={t('common.remove')}
        busy={removeHousehold.isPending}
        onConfirm={() => {
          if (pendingHousehold) {
            void removeHousehold.mutateAsync({ householdId: pendingHousehold.householdId });
          }
          setPendingHousehold(undefined);
        }}
        onClose={() => setPendingHousehold(undefined)}
      />
    </section>
  );
}
